import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import Guid from "common/values/guid/guid";
import MessageNotificationAPIResponse
  from "notifications/entities/message-notification/api/response-contracts/message-notification-api-response";
import MessageNotification from "notifications/entities/message-notification/message-notification";
import NotificationGroupAPIRequest
  from "notifications/entities/notification/api/request-contracts/notification-group-api-request";
import NotificationAPIResponse
  from "notifications/entities/notification/api/response-contracts/notification-api-response";
import Notification from "notifications/entities/notification/notification";
import Session from "users/session/session";
import PaginatedResponse from "common/contracts/paginated-response";
import NotificationQuery from "notifications/entities/notification/api/request-contracts/query-notifications-api-request";
import { assertIsAxiosError } from "common/errors";

export default class NotificationsAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();

  private headersWithJson(): AxiosHeaders {
    return this.headers.concat({ "Content-Type": "application/json" });
  }

  constructor(session: Readonly<Session>) {
    if (!session.authToken) throw new Error("Cannot create NotificationsAPIService without session.");
    this.headers.set(
      "Authorization",
      `Bearer ${session.authToken.value}`
    );
  }

  async markNotificationAsSeen(notification: Partial<Notification>): Promise<Notification> {
    if (!notification.id) throw new Error('Notification id is required');

    try {
      const url = new URL(
        `/notifications/${notification.id.value}/mark-seen`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers
        }
      );
      const responseContract: NotificationAPIResponse = Object.assign(
        new NotificationAPIResponse(),
        response.data
      );
      return responseContract.deserialize();
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new NotificationNotFoundError(notification.id);
      }
      throw new NotificationsAPIServiceError(
        "markNotificationAsSeen",
        error
      );
    }
  }

  async markAllNotificationsAsSeen(): Promise<void> {
    const url = new URL(
      `/notifications/mark-all-seen`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      await axios.post(
        url.toString(),
        {},
        { headers: this.headers }
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new UserNotificationsNotFoundError();
      }
      throw new NotificationsAPIServiceError(
        "markAllNotificationsAsSeen",
        error
      );
    }
  }

  async markNotificationAsUnSeen(notification: Partial<Notification>): Promise<Notification> {
    if (!notification.id) throw new Error('Notification id is required');

    try {
      const url = new URL(
        `/notifications/${notification.id.value}/mark-unseen`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        {},
        { headers: this.headers }
      );
      const responseContract: NotificationAPIResponse = Object.assign(
        new NotificationAPIResponse(),
        response.data
      );
      return responseContract.deserialize();
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new NotificationNotFoundError(notification.id);
      }
      throw new NotificationsAPIServiceError(
        "markNotificationAsUnSeen",
        error
      );
    }
  }

  async markNotificationGroupAsSeen(groupInfo: NotificationGroupAPIRequest): Promise<void> {
    const url = new URL(
      `/notifications/mark-group-seen`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      await axios.post(
        url.toString(),
        groupInfo,
        {
          headers: this.headersWithJson()
        }
      );
    } catch (error) {
      assertIsAxiosError(error);
      throw new NotificationsAPIServiceError(
        "markNotificationGroupAsSeen",
        error
      );
    }
  }

  async archiveNotification(notification: Partial<Notification>): Promise<void> {
    if (!notification.id) throw new Error('Notification id is required');

    try {
      const url = new URL(
        `/notifications/${notification.id.value}/archive`,
        AttorneyHubAPIService.apiBaseUrl
      );
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers
        }
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new NotificationNotFoundError(notification.id);
      }
      throw new NotificationsAPIServiceError(
        "archiveNotification",
        error
      );
    }
  }

  async unArchiveNotification(notification: Partial<Notification>): Promise<void> {
    if (!notification.id) throw new Error('Notification id is required');

    try {
      const url = new URL(
        `/notifications/${notification.id.value}/unarchive`,
        AttorneyHubAPIService.apiBaseUrl
      );
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers
        }
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new NotificationNotFoundError(notification.id);
      }
      throw new NotificationsAPIServiceError(
        "unArchiveNotification",
        error
      );
    }
  }

  async getNotificationById(notificationId: Guid, abortController?: AbortController): Promise<Notification> {
    try {
      const url = new URL(
        `/notifications/${notificationId.value}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.get(
        url.toString(),
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );
      const responseContract: NotificationAPIResponse = Object.assign(
        new NotificationAPIResponse(),
        response.data
      );
      return responseContract.deserialize();
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new NotificationNotFoundError(notificationId);
      }
      throw new NotificationsAPIServiceError(
        "getNotificationById",
        error
      );
    }
  }

  async getNotificationsByQuery(
    query: NotificationQuery,
    abortController?: AbortController
  ): Promise<PaginatedResponse<Notification | MessageNotification>> {
    try {
      const response = await axios.get(
        `${AttorneyHubAPIService.apiBaseUrl}/notifications?${query.asSearchParams().toString()}`,
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );

      const responseData = response.data.map(
        (notification: Partial<MessageNotificationAPIResponse>) => {
          if (!notification?.messageInfo?.id) {
            return Object.assign(
              new NotificationAPIResponse(),
              notification
            ).deserialize();
          } else {
            return Object.assign(
              new MessageNotificationAPIResponse(),
              notification
            ).deserializeAsMessageNotification();
          }
        }
      );

      return PaginatedResponse.from<Notification | MessageNotification>(
        responseData,
        response.headers['x-pagination']
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound)
        throw new UserNotificationsNotFoundError();
      if (error instanceof CanceledError)
        throw error;
      throw new NotificationsAPIServiceError(
        "queryNotifications",
        error
      );
    }
  }
}

// #region Exceptions

export class NotificationsAPIServiceError extends Error {
  public methodName: string;
  public error: Error;

  constructor(methodName: string, error: Error) {
    super(`Error in NotificationsAPIService.${methodName}: ${error}`);
    this.methodName = methodName;
    this.error = error;
  }
}

export class NotificationNotFoundError extends Error {
  public readonly id: Guid;

  constructor(id: Guid) {
    super(`Unable to find notification by id: ${id}`);
    this.id = id;
  }
}

export class UserNotificationsNotFoundError extends Error {
  constructor() {
    super(`User does not have any notifications`);
  }
}

// #endregion
